import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';

import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { FilterService } from '@services/filter.service';
import { PostalCodePipe } from '@pipes/postal-code.pipe';

/** ジオフェンスのポリゴン */
export type ShipmentGeofencePolygonViewModel = {
  /** 緯度 */
  latitude: number;
  /** 経度 */
  longitude: number;
};

/** 配送位置情報のポリゴン形式ジオフェンス */
export type ShipmentPolygonGeofenceViewModel = {
  /** ジオフェンスタイプ */
  geofenceType: 'polygon';
  /** ポリゴンリスト */
  polygonList: ShipmentGeofencePolygonViewModel[];
};

/** 配送位置情報の円形式ジオフェンス */
export type ShipmentCircleGeofenceViewModel = {
  /** ジオフェンスタイプ */
  geofenceType: 'circle';
  /** 円の半径（元の指定した値） */
  circleRadius: number;
  /** 円の半径の単位 */
  circleRadiusUnit: string;
  /** 円の半径（メートル単位） */
  circleRadiusInMeters: number;
};

/** 配送位置情報のジオフェンス */
export type ShipmentGeofenceViewModel =
  | ShipmentCircleGeofenceViewModel
  | ShipmentPolygonGeofenceViewModel;

/** 配送位置情報のレスポンス内容 */
export type ShipmentLocationViewModel = {
  /** 位置情報ID */
  locationId: string;
  /** 位置情報名 */
  locationName: string;
  /** 緯度 */
  latitude: number;
  /** 経度 */
  longitude: number;
  /** 配送もしくはルート設定で使用されている位置情報か */
  isUse?: boolean;
  /** 配送中のルート設定で使用されている位置情報か */
  isShipping?: boolean;
  /** 郵便番号 */
  postalCode?: string;
  /** 住所 */
  address?: string;
  /** ジオフェンス */
  geofence?: ShipmentGeofenceViewModel;
};

/** 配送位置情報一覧のフィルター */
interface ShipmentLocationListFilter {
  locationName?: string;
  postalCode?: string;
  address?: string;
  sortState?: Sort;
  pageIndex?: number;
}

@Component({
  selector: 'app-shipment-location-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DirectivesModule,
    PostalCodePipe,
  ],
  templateUrl: './shipment-location-list-table.component.html',
  styleUrl: './shipment-location-list-table.component.scss',
})
export class ShipmentLocationListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** 位置情報一覧 */
  @Input({ required: true }) public locationList: ShipmentLocationViewModel[] =
    [];

  /** 位置情報の新規登録を通知 */
  @Output() created = new EventEmitter<void>();

  /** 位置情報の更新を通知 */
  @Output() updated = new EventEmitter<ShipmentLocationViewModel>();

  /** 位置情報の削除を通知 */
  @Output() deleted = new EventEmitter<ShipmentLocationViewModel[]>();

  /** CSVエクスポートを通知 */
  @Output() exportCsv = new EventEmitter<void>();

  /** CSVインポートを通知 */
  @Output() imported = new EventEmitter<void>();

  /** 位置情報の選択を通知 */
  @Output() selected = new EventEmitter<ShipmentLocationViewModel>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = [
    'checkbox',
    'locationName',
    'address',
    'postalCode',
    'geofence',
    'operation',
  ] as const;
  /** 選択状態 */
  public selection = new SelectionModel<ShipmentLocationViewModel>(true, []);
  /** 一括削除ボタン表示状態 */
  public isBulkDeleteButtonDisable = true;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<ShipmentLocationViewModel>([]);

  /** テナントID検索キーワード */
  public postalCodeKeyword = '';

  /** テナント名検索キーワード */
  public locationNameKeyword = '';

  /** 会社名検索キーワード */
  public addressKeyword = '';

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'locationName',
    direction: 'asc',
  };

  /** 選択行 */
  public selectedRow: ShipmentLocationViewModel | undefined;

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private filterService: FilterService<
      ShipmentLocationListFilter,
      ShipmentLocationViewModel
    >,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey('shipmentLocationList');
  }

  public ngOnInit(): void {
    this.dataSource.data = this.locationList;
    this.dataSource.sort = this.sort;

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルタ設定
    this.dataSource.filterPredicate = (element: ShipmentLocationViewModel) => {
      let locationName = true;
      if (this.locationNameKeyword) {
        locationName =
          element.locationName
            .trim()
            .toLowerCase()
            .indexOf(this.locationNameKeyword.toLowerCase()) > -1;
      }
      let postalCode = true;
      if (this.postalCodeKeyword) {
        postalCode =
          (element.postalCode ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.postalCodeKeyword.toLowerCase()) > -1;
      }
      let address = true;
      if (this.addressKeyword) {
        address =
          (element.address ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.addressKeyword.toLowerCase()) > -1;
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return postalCode && locationName && address;
    };
    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationList'] !== undefined) {
      const locationList: ShipmentLocationViewModel[] =
        changes['locationList'].currentValue;
      this.dataSource.data = locationList;
      this.selection.clear();
      this.updateBulkDeleteButtonState();

      // ソートされた状態で一番上のデータを取得し、通知する
      if (this.dataSource.sort) {
        const sortedData = this.dataSource.sortData(
          this.dataSource.filteredData,
          this.dataSource.sort,
        );

        if (sortedData.length > 0) {
          this.selectedRow = sortedData[0];
          this.selected.emit(this.selectedRow);
        } else {
          this.selected.emit();
        }
      }
    }
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * マウスオーバー時にポインターと背景色を変更する
   * @param event MouseEvent
   */
  public onMouseEnter(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = 'pointer';
    target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
  }

  /**
   * マウスを離れた際元のスタイルに変更する
   * @param event MouseEvent
   */
  public onMouseLeave(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = '';
    target.style.backgroundColor = '';
  }

  /**
   * テーブル行を選択する。
   * @param selectedRow 選択されたテーブル行
   */
  public selectLocation(selectedRow: ShipmentLocationViewModel): void {
    // 既に選択済みの行が再度選択された場合は選択状態を解除する
    if (selectedRow.locationId === this.selectedRow?.locationId) {
      this.selectedRow = undefined;
    } else {
      this.selectedRow = selectedRow;
    }

    this.selected.emit(this.selectedRow);
  }

  /** 一括削除ボタンの表示状態管理 */
  public updateBulkDeleteButtonState(): void {
    this.isBulkDeleteButtonDisable = this.selection.selected.length === 0;
  }

  /**
   * 一括削除ボタン押下処理
   */
  public bulkDelete() {
    this.deleted.emit(this.selection.selected);
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 位置情報名でのフィルタリングをクリアする。
   */
  public clearLocationNameFilter(): void {
    // フィルタ条件をクリア
    this.locationNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 郵便番号でのフィルタリングをクリアする。
   */
  public clearPostalCodeFilter(): void {
    // フィルタ条件をクリア
    this.postalCodeKeyword = '';

    this.applyFilter();
  }

  /**
   * 住所でのフィルタリングをクリアする。
   */
  public clearAddressFilter(): void {
    // フィルタ条件をクリア
    this.addressKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * エクスポートボタンクリック時処理
   */
  public onExportCsv(): void {
    this.exportCsv.emit();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.locationNameKeyword = filters.locationName || '';
    this.postalCodeKeyword = filters.postalCode || '';
    this.addressKeyword = filters.address || '';
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: ShipmentLocationListFilter = {
      locationName: this.locationNameKeyword,
      postalCode: this.postalCodeKeyword,
      address: this.addressKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
